function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/* eslint-disable jsx-a11y/anchor-is-valid */

/** @jsxRuntime classic */

/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { format, parseISO, isWithinInterval, set } from 'date-fns';
import { useTheme } from 'emotion-theming';
import { phytosanitaryWeb, migratoryWebCitizen, sanitarianRegistersWeb, tdCarWeb } from '../Colors';
import { ArrowRightIcon, CheckCircleIcon, TrashIcon, ExclamationCircleIcon, FileIcon, GripIcon, ListUlIcon, SearchIcon } from '../../icon';
import DropDownCombo from '../forms/inputs/DropDownCombo';
import DatePicker from '../forms/inputs/DatePicker';
import { reducer, mediaQuery } from '../../utils';
import { SpinnerSmall as Spinner } from '../../core';
const ViewContext = /*#__PURE__*/React.createContext();
const themes = {
  migratoryWebCitizen: {
    alpha: migratoryWebCitizen.cerulean,
    beta: migratoryWebCitizen.fireEngineRed,
    gama: migratoryWebCitizen.white,
    delta: migratoryWebCitizen.brightGray,
    epsilon: migratoryWebCitizen.fireEngineRed,
    eta: migratoryWebCitizen.fireEngineRed,
    zeta: migratoryWebCitizen.cerulean,
    iota: migratoryWebCitizen.prussianBlue
  },
  sanitarianRegistersWeb: {
    alpha: sanitarianRegistersWeb.cerulean,
    beta: sanitarianRegistersWeb.fireEngineRed,
    gama: sanitarianRegistersWeb.white,
    delta: sanitarianRegistersWeb.brightGray,
    epsilon: sanitarianRegistersWeb.fireEngineRed,
    eta: sanitarianRegistersWeb.fireEngineRed,
    zeta: sanitarianRegistersWeb.cerulean,
    iota: sanitarianRegistersWeb.prussianBlue
  },
  phytosanitaryWeb: {
    alpha: phytosanitaryWeb.lightSeaGreen,
    beta: phytosanitaryWeb.mantis,
    gama: phytosanitaryWeb.sherpaBlue,
    delta: phytosanitaryWeb.antiFlashWhite,
    epsilon: phytosanitaryWeb.sherpaBlue,
    eta: phytosanitaryWeb.flamingo,
    zeta: phytosanitaryWeb.allports,
    iota: phytosanitaryWeb.sherpaBlue
  },
  tdCarWeb: {
    alpha: tdCarWeb.cerulean,
    beta: tdCarWeb.fireEngineRed,
    gama: tdCarWeb.white,
    delta: tdCarWeb.brightGray,
    epsilon: tdCarWeb.fireEngineRed,
    eta: tdCarWeb.fireEngineRed,
    zeta: tdCarWeb.cerulean,
    iota: tdCarWeb.prussianBlue
  }
};

function isAValidDocument(data) {
  if (!(data === null || data === void 0 ? void 0 : data.validTo)) {
    return true;
  }

  if (data.validFrom && data.validTo) {
    var _data$validFrom, _data$validTo;

    return isWithinInterval(new Date(), {
      start: parseISO((_data$validFrom = data.validFrom) === null || _data$validFrom === void 0 ? void 0 : _data$validFrom.slice(0, 10)),
      end: set(parseISO((_data$validTo = data.validTo) === null || _data$validTo === void 0 ? void 0 : _data$validTo.slice(0, 10)), {
        hours: 23,
        minutes: 59
      })
    });
  }

  return false;
}
/**
 * This component represent Filter.
 * @param {*} t Translation.
 * @param {Function} onSubmit Submit function on the Filter.
 * @param {JSON} documentsDefinitions Document type values for the dropDown.
 * @param {Boolean} isLoading Loading prop when searching.
 */


export function Filter({
  onSubmit,
  documentsDefinitions,
  isLoading = false
}) {
  const {
    theme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const [{
    code,
    validFrom,
    validTo,
    definitionId
  }, dispatch] = React.useReducer(reducer, {
    code: '',
    validFrom: '',
    validTo: '',
    definitionId: ''
  });

  function onChangeCode(evt) {
    dispatch({
      code: evt.target.value
    });
  }

  const onChangeValidTo = React.useCallback(date => {
    dispatch({
      validTo: date
    });
  }, []);
  const onChangeValidFrom = React.useCallback(date => {
    dispatch({
      validFrom: date
    });
  }, []);

  function handleSubmit() {
    onSubmit({
      code,
      validFrom,
      validTo,
      definitionId
    });
  }

  return jsx("div", {
    className: "search-section",
    css: filterStyle
  }, jsx("div", {
    className: "filter"
  }, jsx("div", {
    className: "filter-wrapper combo"
  }, jsx("label", null, t('Tipo de documento'), ":"), jsx(DropDownCombo, {
    items: documentsDefinitions,
    placeholder: t('Seleccionar'),
    value: definitionId,
    style: {
      margin: 0,
      width: '100%'
    },
    onChange: ({
      id
    }) => {
      if (id) {
        dispatch({
          definitionId: id
        });
      } else {
        dispatch({
          definitionId: ''
        });
      }
    }
  }))), jsx("div", {
    className: "filter"
  }, jsx("div", null, jsx("label", null, t('Válido desde'), ":"), jsx(DatePicker, {
    onChange: onChangeValidFrom,
    showMonthDropdown: true,
    showYearDropdown: true,
    showClearDate: true,
    placeholder: t('Ingresar'),
    selectsStart: true,
    value: validFrom,
    maxDate: new Date(`${validTo || format(new Date().setHours(23, 59, 59), 'yyyy/MM/dd')} 00:00:00`)
  }))), jsx("div", {
    className: "filter"
  }, jsx("div", null, jsx("label", null, t('Válido hasta'), ":"), jsx(DatePicker, {
    onChange: onChangeValidTo,
    showMonthDropdown: true,
    showYearDropdown: true,
    showClearDate: true,
    placeholder: t('Ingresar'),
    selectsEnd: true,
    value: validTo,
    minDate: new Date(`${validFrom || format(new Date().setHours(0, 0, 0), 'yyyy/MM/dd')} 00:00:00`)
  }))), jsx("div", {
    className: "filter"
  }, jsx("div", {
    className: "filter-wrapper"
  }, jsx("label", null, t('Búsqueda por código de trámite'), ": "), jsx("div", {
    className: "search"
  }, jsx("input", {
    type: "text",
    placeholder: t('Buscar'),
    value: code,
    onChange: onChangeCode,
    maxLength: 255
  }), jsx(SearchIcon, {
    css: {
      color: themes[theme].alpha,
      position: 'absolute',
      top: 10,
      left: 8
    }
  }))), jsx("button", {
    type: "button",
    onClick: handleSubmit
  }, isLoading ? jsx(Spinner, {
    style: {
      position: 'relative',
      top: 3,
      marginRight: 10
    }
  }) : null, t('Buscar'))));
}

function GridItem({
  data,
  isFunctionary = false,
  onClick,
  wasCreatedByAProcedure
}) {
  var _data$DocumentDefinit, _data$validFrom2, _data$validTo2;

  const {
    theme
  } = useTheme();
  const isDocumentValid = isAValidDocument(data);
  const status = classNames('status', {
    expired: !isDocumentValid
  });
  const trash = classNames('icon-trash', {
    hide: isFunctionary
  });
  return jsx("li", {
    css: gridItemStyle,
    className: "document"
  }, jsx("div", {
    className: "info"
  }, jsx("div", {
    className: "name"
  }, jsx(FileIcon, {
    className: "icon"
  }), jsx("h4", null, data.name)), wasCreatedByAProcedure && jsx("span", {
    className: "item"
  }, jsx("span", {
    className: "solid"
  }, "Tr\xE1mite:"), " ", (data === null || data === void 0 ? void 0 : data.procedureCode) || '-'), jsx("span", {
    className: "item"
  }, jsx("span", {
    className: "solid"
  }, "Tipo:"), " ", data === null || data === void 0 ? void 0 : (_data$DocumentDefinit = data.DocumentDefinition) === null || _data$DocumentDefinit === void 0 ? void 0 : _data$DocumentDefinit.name), jsx("span", {
    className: "item"
  }, jsx("span", {
    className: "solid"
  }, "V\xE1lido desde:"), ' ', (data === null || data === void 0 ? void 0 : data.validFrom) ? format(parseISO((_data$validFrom2 = data.validFrom) === null || _data$validFrom2 === void 0 ? void 0 : _data$validFrom2.slice(0, 10)), 'dd/MM/yyyy') : '', ' ', (data === null || data === void 0 ? void 0 : data.validTo) ? `hasta ${format(parseISO((_data$validTo2 = data.validTo) === null || _data$validTo2 === void 0 ? void 0 : _data$validTo2.slice(0, 10)), 'dd/MM/yyyy')}` : ''), jsx("div", {
    className: status
  }, isDocumentValid ? jsx(React.Fragment, null, jsx(ArrowRightIcon, {
    className: "icon-status",
    css: {
      width: 22,
      height: 22,
      color: themes[theme].zeta
    }
  }), ' ', jsx("span", null, "Documento vigente")) : jsx(React.Fragment, null, jsx(ExclamationCircleIcon, {
    className: "icon-status",
    css: {
      color: themes[theme].eta
    }
  }), jsx("span", null, "Documento vencido"))), isDocumentValid ? jsx("div", {
    className: "status"
  }, jsx(CheckCircleIcon, {
    className: "icon-status",
    css: {
      color: themes[theme].zeta
    }
  }), jsx("span", null, "Documento reutilizable en otros tr\xE1mites")) : null), jsx("div", {
    className: "divider"
  }), jsx("div", {
    className: "actions"
  }, jsx("a", {
    role: "menu",
    tabIndex: 0,
    onClick: onClick
  }, "ver"), !isFunctionary || !wasCreatedByAProcedure && jsx(TrashIcon, {
    className: trash
  })));
}

function ListItem({
  data,
  isFunctionary = false,
  folder,
  onClick,
  wasCreatedByAProcedure,
  ...props
}) {
  var _data$DocumentDefinit2, _data$validFrom3, _data$validTo3;

  const {
    theme
  } = useTheme();
  const isDocumentValid = isAValidDocument(data);
  const status = classNames('status', {
    expired: !isDocumentValid
  });
  const action = classNames('action', {
    hide: isFunctionary
  });
  const row = classNames('row', {
    'style-1': isFunctionary && wasCreatedByAProcedure,
    'style-2': isFunctionary && !wasCreatedByAProcedure,
    'style-3': !isFunctionary && wasCreatedByAProcedure,
    'style-4': !isFunctionary && !wasCreatedByAProcedure
  });
  return jsx("div", _extends({
    className: row
  }, props), jsx("span", {
    className: "bold"
  }, data.name), wasCreatedByAProcedure && jsx("span", {
    style: {
      marginRight: 5
    }
  }, (data === null || data === void 0 ? void 0 : data.procedureCode) || '-'), jsx("span", null, data === null || data === void 0 ? void 0 : (_data$DocumentDefinit2 = data.DocumentDefinition) === null || _data$DocumentDefinit2 === void 0 ? void 0 : _data$DocumentDefinit2.name), jsx("span", null, (data === null || data === void 0 ? void 0 : data.validFrom) ? format(parseISO((_data$validFrom3 = data.validFrom) === null || _data$validFrom3 === void 0 ? void 0 : _data$validFrom3.slice(0, 10)), 'dd/MM/yyyy') : '-'), jsx("span", null, (data === null || data === void 0 ? void 0 : data.validTo) ? format(parseISO((_data$validTo3 = data.validTo) === null || _data$validTo3 === void 0 ? void 0 : _data$validTo3.slice(0, 10)), 'dd/MM/yyyy') : '-'), jsx("span", null, jsx("div", {
    className: status
  }, isDocumentValid ? jsx(React.Fragment, null, jsx(CheckCircleIcon, {
    className: "icon-status",
    css: {
      color: themes[theme].zeta
    }
  }), ' ', jsx("span", null, "Vigente")) : jsx(React.Fragment, null, jsx(ExclamationCircleIcon, {
    className: "icon-status",
    css: {
      color: themes[theme].eta
    }
  }), jsx("span", null, "Vencido")))), jsx("span", null, isDocumentValid ? jsx("div", {
    className: "status"
  }, jsx(CheckCircleIcon, {
    className: "icon-status",
    css: {
      color: themes[theme].zeta
    }
  }), jsx("span", null, "Reutilizable")) : '-'), jsx("span", {
    className: "action"
  }, jsx("a", {
    role: "menu",
    tabIndex: 0,
    onClick: onClick
  }, "ver")), !isFunctionary || !wasCreatedByAProcedure && jsx("span", {
    className: action
  }, jsx(TrashIcon, {
    className: "icon-trash"
  })));
}
/**
 * This component represent an Item.
 * @param {props} t All possible props.
 *
 */


export function Item(props) {
  let viewType = React.useContext(ViewContext);
  /**
   * Si no se usa el contexto que maneja el tipo de vista, por defecto
   * la vista es `grid`
   */

  if (viewType === undefined) {
    viewType = 'grid';
  }

  if (viewType === 'grid') return jsx(GridItem, props);
  return jsx(ListItem, props);
}

function GridWrapper({
  children
}) {
  return jsx("ul", {
    className: "documents grid"
  }, children);
}

function ListWrapper({
  children,
  isFunctionary = false,
  wasCreatedByAProcedure = false
}) {
  const row = classNames('row', {
    'style-1': isFunctionary && wasCreatedByAProcedure,
    'style-2': isFunctionary && !wasCreatedByAProcedure,
    'style-3': !isFunctionary && wasCreatedByAProcedure,
    'style-4': !isFunctionary && !wasCreatedByAProcedure
  });
  return jsx("div", {
    className: "documents list"
  }, jsx("div", {
    className: "head"
  }, jsx("div", {
    className: row
  }, jsx("span", null, "Nombre"), wasCreatedByAProcedure && jsx("span", null, "Tr\xE1mite"), jsx("span", null, "Tipo"), jsx("span", null, "V\xE1lido desde"), jsx("span", null, "V\xE1lido hasta"), jsx("span", null, "Vigencia"), jsx("span", null, "Reutilizable"), jsx("span", null), !isFunctionary && jsx("span", null))), jsx("div", {
    className: "body"
  }, children));
}
/**
 * This component represent a wrapper.
 * @param {*} children What will be display inside the wrapper.
 * @param {*} props All other posible props
 */


export function Wrapper({
  children,
  ...props
}) {
  const viewType = React.useContext(ViewContext);

  if (viewType === 'grid') {
    return jsx(GridWrapper, props, children);
  }

  return jsx(ListWrapper, props, children);
}
/**
 * This component represent a Container.
 * @param {*} children What will be display inside the container.
 */

export function Container({
  children
}) {
  const {
    t
  } = useTranslation();
  const [viewType, setViewType] = React.useState('grid');
  return jsx(ViewContext.Provider, {
    value: viewType
  }, jsx("main", {
    css: mainStyle
  }, jsx("div", {
    className: "view-container"
  }, jsx("ul", {
    className: "view-types"
  }, jsx("li", {
    className: viewType === 'grid' ? 'active' : '',
    onClick: () => setViewType('grid')
  }, jsx(GripIcon, {
    className: "icon-grip icon"
  }), t('Cuadrícula')), jsx("li", {
    className: viewType === 'list' ? 'active' : '',
    onClick: () => setViewType('list')
  }, jsx(ListUlIcon, {
    className: "icon-list icon"
  }), t('Lista')))), children));
}

const documentsStyle = theme => `
  a {
    background: ${themes[theme].beta};
    border-radius: 100px;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 16px;
    color: ${themes[theme].gama};
    text-transform: uppercase;
    padding: 3px 0;
    grid-column-start: 2;
    width: 64px;
    height: 16px;
    text-align: center;
    justify-self: center;
    cursor: pointer;
  }

  .icon-trash {
    width: 14px;
    height: 17px;
    color: ${themes[theme].alpha};
    justify-self: end;
  }

  .status.expired {
    .icon-status {
      color: ${themes[theme].eta}
    }
    span {
      color:${themes[theme].eta};
    }
  }

  .status {
    display: flex;
    align-items: center;
    .icon-status {
      width: 16px;
      height: 16px;
      color: #0f7cb5;
    }

    span {
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 20px;
      color: ${themes[theme].zeta};
      margin-left: 10px;
    }
  }
`;

const mainStyle = ({
  theme = 'migratoryWebCitizen'
}) => css`
  background-color: ${themes[theme].delta};
  padding: 25px 37px 79px 37px;
  border-radius: 6px;
  margin-top: 28px;

  .view-container {
    display: flex;
    justify-content: flex-end;

    .view-types {
      display: flex;
      margin: 0;
      padding: 0;
      list-style: none;
      border: 1px solid ${themes[theme].beta};
      width: 246px;
      float: right;

      li {
        padding: 8px 13px;
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 20px;
        color: ${themes[theme].epsilon};
        display: inline-flex;
        justify-content: center;
        align-items: center;
        width: 123px;
        flex-wrap: wrap;
        cursor: pointer;

        .icon-grip {
          color: ${themes[theme].epsilon};
          width: 21px;
          height: 21px;
          margin-right: 6px;
        }

        .icon-list {
          color: ${themes[theme].epsilon};
          width: 22px;
          height: 18px;
          margin-right: 6px;
        }
      }

      li.active {
        background-color: ${themes[theme].beta};
        color: ${themes[theme].gama};
        .icon {
          color: ${themes[theme].gama};
        }
      }
    }
  }

  .documents {
    ${documentsStyle(theme)}
  }

  .hide {
    display: none !important;
  }

  .documents.list {
    margin-top: 25px;

    .row {
      display: grid;
    }

    .row.style-1 {
      grid-template-columns: 20% 15% 20% 10% 10% 10% 10% 5%;
    }

    .row.style-2 {
      grid-template-columns: 25% 25% 10% 10% 10% 10% 10%;
    }

    .row.style-3 {
      grid-template-columns: 20% 12% 18% 10% 10% 10% 10% 5% 5%;
    }

    .row.style-4 {
      grid-template-columns: 20% 20% 20% 10% 10% 10% 5% 5%;
    }

    .body {
      font-style: normal;
      font-weight: normal;
      font-size: 16px;
      line-height: 20px;
      color: #313131;

      .action {
        display: flex;
        justify-content: center;

        a {
          display: inline-block;
          cursor: pointer;
        }
      }

      .row {
        border-bottom: 0.5px solid #aacde0;
        padding: 22px 0px;
        box-sizing: border-box;

        span {
          margin-right: 6px;
        }
      }

      .bold {
        font-style: normal;
        font-weight: 600;
        font-size: 14px;
        line-height: 16px;
        color: #08415c;
      }
    }

    .head {
      span {
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 20px;
        color: ${themes[theme].zeta};
      }
    }
  }

  .documents.grid {
    list-style: none;
    margin: 23px 0 0 0;
    padding: 0;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 35px;
    ${mediaQuery[0]} {
      grid-template-columns: 1fr;
      margin: 0;
      gap: 12px;
    }
  }
`;

const gridItemStyle = ({
  theme = 'migratoryWebCitizen'
}) => css`
  background-color: #fff;
  border-radius: 10px;
  ${documentsStyle(theme)}

  .info {
    padding: 18px 24px;
    min-height: 154px;

    .name {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
    }

    h4 {
      margin: 0;
      font-style: normal;
      font-weight: 600;
      font-size: 14px;
      line-height: 16px;
      color: #08415c;
    }

    .icon {
      width: 24px;
      height: 29px;
      color: ${themes[theme].iota};
      margin-right: 13px;
    }

    .item {
      display: block;
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 25px;
      color: #313131;

      .solid {
        font-weight: bolder;
      }
    }

    .status {
      margin-top: 10px;
    }
  }

  .divider {
    border-top: 0.5px solid #aacde0;
  }

  .actions {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: center;
    padding: 12px 26px;
  }
`;

const filterStyle = ({
  theme = 'migratoryWebCitizen'
}) => css`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  width: 100%;
  gap: 12px;
  ${mediaQuery[0]} {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr);
    overflow: auto;
  }

  .filter {
    .filter-wrapper {
      display: flex;
      flex-direction: column;
      height: 100%;
      ${mediaQuery[0]} {
        width: 100%;
      }

      .search {
        width: 100%;
        position: relative;
        input {
          background: #ffffff;
          border: 1px solid #0f7cb5;
          box-sizing: border-box;
          border-radius: 4px;
          height: 34px;
          padding: 0 30px;
          ${mediaQuery[0]} {
            height: 46px;
          }
        }

        .icon {
          width: 18px;
          height: 18px;
          color: #0f7cb5;
          position: absolute;
          top: 8px;
          left: 8px;
          ${mediaQuery[0]} {
            top: 14px;
          }
        }
      }
    }

    div {
      label {
        font-style: normal;
        font-weight: normal;
        font-size: 12px;
        line-height: 16px;
        color: #282828;
        padding-left: 10px;
      }
    }

    .combo {
      input {
        height: 34px;
        border: 1px solid #0f7cb5;
        ${mediaQuery[0]} {
          height: 46px;
        }
      }
    }
    .react-datepicker-wrapper {
      input {
        background-color: #fff;
        border: 1px solid #0f7cb5;
        box-shadow: 0px 7px 64px rgba(0, 0, 0, 0.07);
        border-radius: 6px;
        height: 34px;
        padding: 14px 16px;
        font-size: 15px;
        color: #0f7cb5;
        width: 100%;
        ${mediaQuery[0]} {
          height: 46px;
        }
      }
    }
    :last-of-type {
      grid-column: span 3;
      display: flex;
      align-items: flex-end;
      ${mediaQuery[0]} {
        grid-column: unset;
        flex-wrap: wrap;
        overflow: auto;
      }

      div {
        width: 80%;
        display: flex;
        flex-direction: column;
        ${mediaQuery[0]} {
          width: 100%;
          height: 62px;
        }
      }

      button {
        width: 20%;
        margin-left: 20px;
        background: ${themes[theme].beta};
        border-radius: 6px;
        border: ${themes[theme].beta};
        font-style: normal;
        font-weight: 600;
        font-size: 14px;
        line-height: 22px;
        text-transform: uppercase;
        color: ${themes[theme].gama};
        min-height: 34px;
        ${mediaQuery[0]} {
          width: 100%;
          margin-left: 0px;
          margin-top: 23px;
          height: 38px;
          position: relative;
        }
      }
    }
  }
`;